import React, { useState } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ChartBarIcon } from '@heroicons/react/24/outline';

const VIEW_OPTIONS = ['Totals', 'Average', 'Trend'];
const TOP_OPTIONS = ['Top 5', 'Top 10', 'All'];

const ENGINE_COLORS = {
  ChatGPT: '#10B981',
  Perplexity: '#3B82F6',
  'Google AI': '#F59E0B',
  Copilot: '#8B5CF6',
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="w-3 h-3 rounded-full inline-block"
            style={{ backgroundColor: ENGINE_COLORS[d.engine] || '#6B7280' }}
          />
          <span className="font-semibold text-gray-900">{d.engine}</span>
        </div>
        <p className="text-gray-600">Prompt Coverage: <span className="font-medium text-gray-900">{d.promptCoverage}%</span></p>
        <p className="text-gray-600">Citation Frequency: <span className="font-medium text-gray-900">{d.citationFrequency}</span></p>
        <p className="text-gray-600">Citation Share: <span className="font-medium text-gray-900">{d.share}%</span></p>
      </div>
    );
  }
  return null;
};

const CustomDot = (props) => {
  const { cx, cy, payload } = props;
  const color = ENGINE_COLORS[payload.engine] || '#6B7280';
  const radius = Math.max(16, Math.min(32, payload.share * 0.6));

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={color}
        fillOpacity={0.2}
        stroke={color}
        strokeWidth={2}
      />
      <circle cx={cx} cy={cy} r={6} fill={color} />
      <text
        x={cx}
        y={cy - radius - 6}
        textAnchor="middle"
        fill={color}
        fontSize={11}
        fontWeight={600}
      >
        {payload.engine}
      </text>
    </g>
  );
};

const AICitationShare = ({ data = [], loading = false }) => {
  const [viewType, setViewType] = useState('Totals');
  const [topFilter, setTopFilter] = useState('Top 5');

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 h-full" role="status" aria-label="Loading citation share data">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between">
            <div className="h-6 bg-gray-200 rounded w-36"></div>
            <div className="flex gap-2">
              <div className="h-8 bg-gray-200 rounded w-20"></div>
              <div className="h-8 bg-gray-200 rounded w-20"></div>
            </div>
          </div>
          <div className="h-48 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">AI Citation Share</h3>
        <div className="flex items-center gap-2">
          <select
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Citation share view type"
          >
            {VIEW_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <select
            value={topFilter}
            onChange={(e) => setTopFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Citation share top filter"
          >
            {TOP_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Chart */}
      {data.length > 0 ? (
        <div className="flex-1 min-h-0" role="img" aria-label="AI Citation Share scatter chart showing citation distribution across AI engines">
          <ResponsiveContainer width="100%" height={220}>
            <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis
                type="number"
                dataKey="promptCoverage"
                name="Prompt Coverage"
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 11, fill: '#9CA3AF' }}
                axisLine={{ stroke: '#E5E7EB' }}
                label={{
                  value: 'Prompt Coverage',
                  position: 'bottom',
                  offset: 0,
                  style: { fontSize: 11, fill: '#6B7280' },
                }}
              />
              <YAxis
                type="number"
                dataKey="citationFrequency"
                name="Citation Frequency"
                tick={{ fontSize: 11, fill: '#9CA3AF' }}
                axisLine={{ stroke: '#E5E7EB' }}
                width={35}
                label={{
                  value: 'Citation Freq.',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 10,
                  style: { fontSize: 11, fill: '#6B7280' },
                }}
              />
              <ZAxis type="number" dataKey="share" range={[100, 800]} />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Scatter data={data} shape={<CustomDot />}>
                {data.map((entry, idx) => (
                  <Cell key={idx} fill={ENGINE_COLORS[entry.engine] || '#6B7280'} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-2" role="list" aria-label="AI engine legend">
            {data.map((entry) => (
              <div key={entry.engine} className="flex items-center gap-1.5" role="listitem">
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ backgroundColor: ENGINE_COLORS[entry.engine] || '#6B7280' }}
                />
                <span className="text-xs text-gray-500">{entry.engine}</span>
                <span className="text-xs font-semibold text-gray-700">{entry.share}%</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
          <ChartBarIcon className="h-10 w-10 text-gray-300 mb-3" aria-hidden="true" />
          <p className="text-sm font-medium text-gray-500">Citation share data will appear here.</p>
          <p className="text-xs text-gray-400 mt-1">Track which AI engines cite your content most.</p>
        </div>
      )}
    </div>
  );
};

export default AICitationShare;
